import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Descriptions, Drawer, Input, InputNumber, Select, Space, Table, Tabs, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import {
  OPS_TOOL_DSP_DRIVER_CLEARING,
  OPS_TOOL_DSP_DRIVER_DETAIL,
  OPS_TOOL_DSP_DRIVER_ENTRY,
  OPS_TOOL_DSP_DRIVER_SUMMARY,
} from './graphql/dspDriverBillGraphql';

type SummaryType = 'BY_THREE_CODE' | 'IGNORE_THREE_CODE';
type YesOrNo = 'YES' | 'NO';

type SummaryRow = {
  deliveredDate: number | null;
  driverId: number | null;
  oneSortingCode: string | null;
  twoSortingCode: string | null;
  threeSortingCode: string | null;
  totalPackageCount: number | null;
  fullPricePackageCount: number | null;
  discountPricePackageCount: number | null;
  originalPackageTotalAmount: string | null;
  fullPriceDeliveredAmount: string | null;
  discountDeliveredAmount: string | null;
  settlementAmount: string | null;
};

type DetailRow = {
  id: number;
  deliveredDate: number | null;
  businessId: string | null;
  billId: number | null;
  hubId: number | null;
  dspId: number | null;
  driverId: number | null;
  driverPhone: string | null;
  driverName: string | null;
  oneSortingCode: string | null;
  twoSortingCode: string | null;
  threeSortingCode: string | null;
  settlementAmount: string | null;
  originAmount: string | null;
  discountAmount: string | null;
  discountFlag: YesOrNo | null;
  normalAddress: string | null;
  groupId: string | null;
  groupSort: number | null;
};

type ClearingRow = {
  id: number;
  settlementClientId: number | null;
  settlementItemId: number | null;
  settlementItemCode: number | null;
  settlementItemName: string | null;
  amount: string | null;
  amountHm: number | null;
  businessId: string | null;
  otherBusinessId: string | null;
  status: number | null;
  pricingId: number | null;
  businessTime: string | null;
  settlementTime: string | null;
  clearingNode: string | null;
  isDeleted: boolean | null;
  createdAt: string | null;
};

type EntryRow = {
  id: number;
  billId: number | null;
  clearingId: number | null;
  businessId: string | null;
  settlementFeeItem: number | null;
  amount: string | null;
  amountHm: number | null;
  createdAt: string | null;
};

const SUMMARY_TYPE_OPTIONS = [
  { value: 'BY_THREE_CODE', label: '按三段码' },
  { value: 'IGNORE_THREE_CODE', label: '忽略三段码' },
];

const MAX_BUSINESS_IDS = 200;

const usd = (v: string | null): string => (v == null ? '-' : `$${v}`);

const dateStr = (v: number | null): string => {
  if (v == null) return '-';
  const s = String(v);
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s;
};

const DspDriverBillPage: React.FC = () => {
  const [driverId, setDriverId] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>([
    dayjs().subtract(6, 'day'),
    dayjs(),
  ]);
  const [summaryType, setSummaryType] = useState<SummaryType>('BY_THREE_CODE');
  const [summaryPage, setSummaryPage] = useState(1);
  const [summarySize, setSummarySize] = useState(20);

  const [activeTab, setActiveTab] = useState('summary');

  const [businessIdsText, setBusinessIdsText] = useState('');
  const [detailDrill, setDetailDrill] = useState<{
    driverId?: number;
    deliveredDate?: number;
    oneSortingCode?: string;
    twoSortingCode?: string;
    threeSortingCode?: string;
    discountFlag?: YesOrNo;
    businessIds?: string[];
  } | null>(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailSize, setDetailSize] = useState(20);

  const [drawerRow, setDrawerRow] = useState<DetailRow | null>(null);

  const [loadSummary, { data: summaryData, loading: summaryLoading }] = useLazyQuery(OPS_TOOL_DSP_DRIVER_SUMMARY);
  const [loadDetail, { data: detailData, loading: detailLoading }] = useLazyQuery(OPS_TOOL_DSP_DRIVER_DETAIL);
  const [loadClearing, { data: clearingData, loading: clearingLoading }] = useLazyQuery(OPS_TOOL_DSP_DRIVER_CLEARING);
  const [loadEntry, { data: entryData, loading: entryLoading }] = useLazyQuery(OPS_TOOL_DSP_DRIVER_ENTRY);

  const parsedBusinessIds = useMemo(
    () =>
      businessIdsText
        .split(/[\s,，]+/)
        .map(s => s.trim())
        .filter(Boolean),
    [businessIdsText]
  );

  const fetchSummary = useCallback(
    (targetPage: number, targetSize: number) => {
      if (driverId == null) {
        message.warning('请输入司机ID');
        return;
      }
      if (!dateRange?.[0] || !dateRange?.[1]) {
        message.warning('请选择妥投日期范围');
        return;
      }
      loadSummary({
        variables: {
          filters: {
            driverId,
            deliveredDateStart: Number(dayjs(dateRange[0]).format('YYYYMMDD')),
            deliveredDateEnd: Number(dayjs(dateRange[1]).format('YYYYMMDD')),
            summaryType,
          },
          pageRequest: { page: targetPage, size: targetSize },
        },
      });
    },
    [driverId, dateRange, summaryType, loadSummary]
  );

  const fetchDetail = useCallback(
    (drill: NonNullable<typeof detailDrill>, targetPage: number, targetSize: number) => {
      loadDetail({
        variables: {
          filters: {
            driverId: drill.driverId ?? null,
            deliveredDate: drill.deliveredDate ?? null,
            oneSortingCode: drill.oneSortingCode ?? null,
            twoSortingCode: drill.twoSortingCode ?? null,
            threeSortingCode: drill.threeSortingCode ?? null,
            discountFlag: drill.discountFlag ?? null,
            businessIds: drill.businessIds ?? null,
          },
          pageRequest: { page: targetPage, size: targetSize },
        },
      });
    },
    [loadDetail]
  );

  const handleSearchSummary = () => {
    setSummaryPage(1);
    fetchSummary(1, summarySize);
  };

  const drillToDetail = (row: SummaryRow, discountFlag: YesOrNo) => {
    const drill = {
      driverId: row.driverId ?? undefined,
      deliveredDate: row.deliveredDate ?? undefined,
      oneSortingCode: row.oneSortingCode ?? undefined,
      twoSortingCode: row.twoSortingCode ?? undefined,
      threeSortingCode: row.threeSortingCode ?? undefined,
      discountFlag,
    };
    setDetailDrill(drill);
    setDetailPage(1);
    setBusinessIdsText('');
    setActiveTab('detail');
    fetchDetail(drill, 1, detailSize);
  };

  const handleSearchDetailByBusinessIds = () => {
    if (parsedBusinessIds.length === 0) {
      message.warning('请输入至少一个运单号');
      return;
    }
    if (parsedBusinessIds.length > MAX_BUSINESS_IDS) {
      message.warning(`运单号数量不能超过 ${MAX_BUSINESS_IDS} 个`);
      return;
    }
    const drill = { businessIds: parsedBusinessIds };
    setDetailDrill(drill);
    setDetailPage(1);
    fetchDetail(drill, 1, detailSize);
  };

  const openDrawer = (row: DetailRow) => {
    setDrawerRow(row);
    if (row.businessId) {
      loadClearing({ variables: { businessId: row.businessId } });
      if (row.billId != null) {
        loadEntry({ variables: { billId: row.billId, businessId: row.businessId } });
      }
    }
  };

  const summaryResult = summaryData?.opsToolDspDriverSummary;
  const detailResult = detailData?.opsToolDspDriverDetail;

  useEffect(() => {
    if (summaryResult && !summaryResult.success) {
      message.error(summaryResult.message || '查询汇总失败');
    }
  }, [summaryResult]);

  useEffect(() => {
    if (detailResult && !detailResult.success) {
      message.error(detailResult.message || '查询运单明细失败');
    }
  }, [detailResult]);

  const summaryRows: SummaryRow[] = summaryResult?.content ?? [];
  const detailRows: DetailRow[] = detailResult?.content ?? [];
  const clearingRows: ClearingRow[] = clearingData?.opsToolDspDriverClearing ?? [];
  const entryRows: EntryRow[] = entryData?.opsToolDspDriverEntry ?? [];

  const summaryColumns: ColumnsType<SummaryRow> = [
    { title: '妥投日期', dataIndex: 'deliveredDate', width: 120, render: dateStr },
    { title: '司机ID', dataIndex: 'driverId', width: 100 },
    { title: '一段码', dataIndex: 'oneSortingCode', width: 100, render: v => v ?? '-' },
    { title: '二段码', dataIndex: 'twoSortingCode', width: 100, render: v => v ?? '-' },
    { title: '三段码', dataIndex: 'threeSortingCode', width: 100, render: v => v ?? '-' },
    { title: '包裹总数', dataIndex: 'totalPackageCount', width: 100 },
    {
      title: '全价单数',
      dataIndex: 'fullPricePackageCount',
      width: 100,
      render: (v: number | null, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => drillToDetail(row, 'NO')}>
          {v ?? 0}
        </Button>
      ),
    },
    {
      title: '折扣单数',
      dataIndex: 'discountPricePackageCount',
      width: 100,
      render: (v: number | null, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => drillToDetail(row, 'YES')}>
          {v ?? 0}
        </Button>
      ),
    },
    { title: '原始包裹总价', dataIndex: 'originalPackageTotalAmount', width: 120, render: usd },
    { title: '全价妥投金额', dataIndex: 'fullPriceDeliveredAmount', width: 120, render: usd },
    { title: '折扣妥投金额', dataIndex: 'discountDeliveredAmount', width: 120, render: usd },
    { title: '结算金额', dataIndex: 'settlementAmount', width: 120, render: usd },
  ];

  const detailColumns: ColumnsType<DetailRow> = [
    { title: '运单号', dataIndex: 'businessId', width: 160, render: v => v ?? '-' },
    { title: '妥投日期', dataIndex: 'deliveredDate', width: 120, render: dateStr },
    { title: '司机', dataIndex: 'driverName', width: 100, render: v => v ?? '-' },
    { title: '一段码', dataIndex: 'oneSortingCode', width: 90, render: v => v ?? '-' },
    { title: '二段码', dataIndex: 'twoSortingCode', width: 90, render: v => v ?? '-' },
    { title: '三段码', dataIndex: 'threeSortingCode', width: 90, render: v => v ?? '-' },
    { title: '结算金额', dataIndex: 'settlementAmount', width: 110, render: usd },
    { title: '原金额', dataIndex: 'originAmount', width: 110, render: usd },
    { title: '折扣金额', dataIndex: 'discountAmount', width: 110, render: usd },
    {
      title: '折扣',
      dataIndex: 'discountFlag',
      width: 80,
      render: (v: YesOrNo | null) =>
        v === 'YES' ? <Tag color="orange">折扣</Tag> : v === 'NO' ? <Tag color="green">全价</Tag> : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => openDrawer(row)}>
          详情
        </Button>
      ),
    },
  ];

  const clearingColumns: ColumnsType<ClearingRow> = [
    { title: 'ID', dataIndex: 'id', width: 100 },
    { title: '费用项', dataIndex: 'settlementItemName', render: v => v ?? '-' },
    { title: '费用项Code', dataIndex: 'settlementItemCode', width: 120, render: v => v ?? '-' },
    { title: '金额', dataIndex: 'amount', width: 100, render: usd },
    { title: '金额HM(毫)', dataIndex: 'amountHm', width: 110, render: (v: number | null) => v ?? '-' },
    { title: '状态', dataIndex: 'status', width: 80, render: v => v ?? '-' },
    { title: '清分节点', dataIndex: 'clearingNode', render: v => v ?? '-' },
    { title: '结算时间', dataIndex: 'settlementTime', width: 180, render: v => v ?? '-' },
    {
      title: '是否删除',
      dataIndex: 'isDeleted',
      width: 90,
      render: (v: boolean | null) =>
        v === true ? <Tag color="red">已删除</Tag> : v === false ? <Tag color="green">有效</Tag> : '-',
    },
    { title: '创建时间', dataIndex: 'createdAt', width: 180, render: v => v ?? '-' },
  ];

  const entryColumns: ColumnsType<EntryRow> = [
    { title: 'ID', dataIndex: 'id', width: 100 },
    { title: '账单ID', dataIndex: 'billId', width: 120 },
    { title: '清分ID', dataIndex: 'clearingId', width: 120, render: v => v ?? '-' },
    { title: '费用项', dataIndex: 'settlementFeeItem', width: 120, render: v => v ?? '-' },
    { title: '金额', dataIndex: 'amount', width: 100, render: usd },
    { title: '金额HM(毫)', dataIndex: 'amountHm', width: 110, render: (v: number | null) => v ?? '-' },
    { title: '创建时间', dataIndex: 'createdAt', width: 180, render: v => v ?? '-' },
  ];

  return (
    <Card title="司机结算账单" bordered={false}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <InputNumber
            placeholder="司机ID"
            style={{ width: 160 }}
            value={driverId}
            onChange={value => setDriverId(value ?? undefined)}
          />
          <DatePicker.RangePicker
            value={dateRange}
            onChange={range => setDateRange(range)}
            placeholder={['妥投开始', '妥投结束']}
          />
          <Select<SummaryType>
            style={{ width: 160 }}
            options={SUMMARY_TYPE_OPTIONS}
            value={summaryType}
            onChange={setSummaryType}
          />
          <Button type="primary" onClick={handleSearchSummary}>
            查询
          </Button>
        </Space>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'summary',
              label: '结算汇总',
              children: (
                <Table<SummaryRow>
                  rowKey={(r, i) => `${r.driverId}-${r.deliveredDate}-${r.oneSortingCode}-${r.twoSortingCode}-${r.threeSortingCode}-${i}`}
                  loading={summaryLoading}
                  columns={summaryColumns}
                  dataSource={summaryRows}
                  scroll={{ x: 1400 }}
                  pagination={{
                    current: summaryPage,
                    pageSize: summarySize,
                    total: summaryResult?.totalElements ?? 0,
                    showSizeChanger: true,
                    showTotal: total => `共 ${total} 条`,
                    onChange: (p, size) => {
                      setSummaryPage(p);
                      setSummarySize(size);
                      fetchSummary(p, size);
                    },
                  }}
                />
              ),
            },
            {
              key: 'detail',
              label: '运单列表',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Space wrap align="start">
                    <Input.TextArea
                      placeholder="运单号（多个用空格、逗号或换行分隔）"
                      style={{ width: 360 }}
                      autoSize={{ minRows: 1, maxRows: 4 }}
                      value={businessIdsText}
                      onChange={e => setBusinessIdsText(e.target.value)}
                    />
                    <Button type="primary" onClick={handleSearchDetailByBusinessIds}>
                      按运单号查询
                    </Button>
                  </Space>
                  <Table<DetailRow>
                    rowKey="id"
                    loading={detailLoading}
                    columns={detailColumns}
                    dataSource={detailRows}
                    scroll={{ x: 1300 }}
                    pagination={{
                      current: detailPage,
                      pageSize: detailSize,
                      total: detailResult?.totalElements ?? 0,
                      showSizeChanger: true,
                      showTotal: total => `共 ${total} 条`,
                      onChange: (p, size) => {
                        setDetailPage(p);
                        setDetailSize(size);
                        if (detailDrill) fetchDetail(detailDrill, p, size);
                      },
                    }}
                  />
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Drawer
        title={`运单详情 ${drawerRow?.businessId ?? ''}`}
        width={900}
        open={Boolean(drawerRow)}
        onClose={() => setDrawerRow(null)}
        destroyOnClose
      >
        {drawerRow && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions title="运单详情" bordered size="small" column={2}>
              <Descriptions.Item label="运单号">{drawerRow.businessId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="妥投日期">{dateStr(drawerRow.deliveredDate)}</Descriptions.Item>
              <Descriptions.Item label="账单ID">{drawerRow.billId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="司机">{drawerRow.driverName ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="司机手机">{drawerRow.driverPhone ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="Hub">{drawerRow.hubId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="DSP">{drawerRow.dspId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="一段码">{drawerRow.oneSortingCode ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="二段码">{drawerRow.twoSortingCode ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="三段码">{drawerRow.threeSortingCode ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="结算金额">{usd(drawerRow.settlementAmount)}</Descriptions.Item>
              <Descriptions.Item label="原金额">{usd(drawerRow.originAmount)}</Descriptions.Item>
              <Descriptions.Item label="折扣金额">{usd(drawerRow.discountAmount)}</Descriptions.Item>
              <Descriptions.Item label="折扣标识">
                {drawerRow.discountFlag === 'YES' ? '折扣' : drawerRow.discountFlag === 'NO' ? '全价' : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="分组ID">{drawerRow.groupId ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="分组排序">{drawerRow.groupSort ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="标准化地址" span={2}>{drawerRow.normalAddress ?? '-'}</Descriptions.Item>
            </Descriptions>

            <div>
              <h4>入账数据</h4>
              <Table<EntryRow>
                rowKey="id"
                size="small"
                loading={entryLoading}
                columns={entryColumns}
                dataSource={entryRows}
                pagination={false}
              />
            </div>

            <div>
              <h4>清分数据</h4>
              <Table<ClearingRow>
                rowKey="id"
                size="small"
                loading={clearingLoading}
                columns={clearingColumns}
                dataSource={clearingRows}
                scroll={{ x: 900 }}
                pagination={false}
              />
            </div>
          </Space>
        )}
      </Drawer>
    </Card>
  );
};

export default DspDriverBillPage;
